import { Icon } from '#/components/Icon/Icon';
import type { IconName } from '#/components/Icon/IconName';
import { clickActionsAttributes } from '#/snippets/click-actions.attributes';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import {
  DropdownFoldBottomMenuPanel,
  DropdownFoldMenu_Box_1,
  dropdownFoldMenuButtonItemWidthVar,
  dropdownFoldMenuColumnsCountVar,
  dropdownFoldMenuDesktopLeftVar,
  DropdownFoldMenuHolder,
  DropdownFoldMenuItem,
  DropdownFoldMenuLeftIems,
  DropdownFoldMenuPanel,
  DropdownFoldMenuPanelTitle,
  dropdownFoldMenuRightImageHolderClassName,
  DropdownFoldMenuRightItemDescription,
  DropdownFoldMenuRightItems,
  DropdownFoldMenuRightItemTitle,
  DropdownMobileBackdrop,
} from './DropdownFoldMenu.styled';

type Props = {
  left: {
    title: string;
    width?: number;
    columnCountOverride?: number;
    items: {
      iconName: IconName;
      title: string;
      href: string;
      qaId: string;
      trackClicked: { name: string; context: string };
      fullPageReload?: boolean;
    }[];
  };
  right:
    | {
        title: string;
        items: {
          image: string;
          imageHover: string;
          title: string;
          description: string;
          href: string;
          qaId: string;
          trackClicked: { name: string; context: string };
        }[];
      }
    | undefined;
  bottom?: React.ReactNode;
};

export function DropdownFoldMenu({ left, right, bottom }: Props) {
  return (
    <DropdownFoldMenuHolder
      style={{
        [dropdownFoldMenuDesktopLeftVar]: right ? '-92px' : 0,
        [dropdownFoldMenuButtonItemWidthVar]: left.width
          ? `${left.width}px`
          : undefined,
      }}
    >
      <DropdownFoldMenu_Box_1>
        <DropdownFoldMenuPanel>
          <DropdownFoldMenuPanelTitle>{left.title}</DropdownFoldMenuPanelTitle>

          <DropdownFoldMenuLeftIems
            style={{
              [dropdownFoldMenuColumnsCountVar]: left.columnCountOverride
                ? left.columnCountOverride
                : Math.ceil(left.items.length / 3),
            }}
          >
            {left.items.map((item) => (
              <DropdownFoldMenuItem
                key={item.title}
                type="link"
                href={item.href}
                qaId={item.qaId}
                anchorType={item.fullPageReload ? 'native' : 'router'}
                clickActions={{
                  track: {
                    name: item.trackClicked.name,
                    properties: {
                      context: item.trackClicked.context,
                    },
                  },
                  closeRichDetailsFromWithin: true,
                }}
                data-variant="left"
              >
                <Icon name={item.iconName} size={16} />
                <span>{item.title}</span>
              </DropdownFoldMenuItem>
            ))}
          </DropdownFoldMenuLeftIems>
        </DropdownFoldMenuPanel>

        {right && (
          <DropdownFoldMenuPanel>
            <DropdownFoldMenuPanelTitle>
              {right.title}
            </DropdownFoldMenuPanelTitle>

            <DropdownFoldMenuRightItems>
              {right.items.map((item) => (
                <DropdownFoldMenuItem
                  key={item.title}
                  type="link"
                  href={item.href}
                  qaId={item.qaId}
                  data-variant="right"
                  clickActions={{
                    track: {
                      name: item.trackClicked.name,
                      properties: {
                        context: item.trackClicked.context,
                      },
                    },
                    closeRichDetailsFromWithin: true,
                  }}
                >
                  <div className={dropdownFoldMenuRightImageHolderClassName}>
                    <img
                      src={getStaticImageSrc(item.image)}
                      alt=""
                      loading="lazy"
                    />

                    <img
                      src={getStaticImageSrc(item.imageHover)}
                      alt=""
                      loading="lazy"
                    />
                  </div>

                  <DropdownFoldMenuRightItemTitle>
                    {item.title}
                  </DropdownFoldMenuRightItemTitle>

                  <DropdownFoldMenuRightItemDescription>
                    {item.description}
                  </DropdownFoldMenuRightItemDescription>
                </DropdownFoldMenuItem>
              ))}
            </DropdownFoldMenuRightItems>
          </DropdownFoldMenuPanel>
        )}
      </DropdownFoldMenu_Box_1>

      {bottom && (
        <DropdownFoldBottomMenuPanel>{bottom}</DropdownFoldBottomMenuPanel>
      )}

      <DropdownMobileBackdrop
        // oxlint-disable-next-line react/jsx-props-no-spreading
        {...clickActionsAttributes({
          closeRichDetailsFromWithin: true,
        })}
      />
    </DropdownFoldMenuHolder>
  );
}
